import React from "react";
import { CheckCircle2, ArrowRight, CreditCard, Banknote } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PRICING_FEATURES } from "../data/mock";
import { useTranslation } from "react-i18next";

export const PricingSection = () => {
  const { t } = useTranslation();
  return (
    <section className="py-20 lg:py-28 bg-[#F5F1EA]/50" id="precios">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 section-reveal">
          <Badge className="bg-white text-[#C8A24D] border-[#E8D5A0] hover:bg-white mb-6 py-1.5 px-4 text-xs font-semibold tracking-wide uppercase">
            {t("pricing.badge")}
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-[42px] text-[#0C2340] leading-tight mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {t("pricing.title")}
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-lg mx-auto section-reveal">
          <div className="relative">
            {/* Glow effect behind card */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#C8A24D]/20 via-[#E8D5A0]/20 to-[#C8A24D]/20 rounded-[28px] blur-lg" />
            
            <div className="relative bg-white rounded-3xl border border-[#E8E4DC] overflow-hidden">
              {/* Top accent */}
              <div className="h-1.5 bg-gradient-to-r from-[#C8A24D] via-[#E8D5A0] to-[#C8A24D]" />
              
              <div className="p-8 sm:p-10">
                {/* Price Header */}
                <div className="text-center mb-8">
                  <p className="text-sm text-[#7A7A8A] font-medium uppercase tracking-wider mb-3">{t("pricing.openingLabel")}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-6xl sm:text-7xl font-bold text-[#0C2340]" style={{ fontFamily: "'DM Serif Display', serif" }}>50</span>
                    <span className="text-2xl text-[#C8A24D] font-semibold">€</span>
                    <span className="text-lg text-[#7A7A8A] ml-1">/ {t("pricing.recurrence", "quincena")}</span>
                  </div>
                  <p className="text-[#4A5568] mt-3 text-sm">{t("pricing.openingDesc")}</p>
                </div>

                {/* Payment Plan */}
                <div className="bg-[#F5F1EA] rounded-2xl p-5 mb-8 border border-[#E8D5A0]/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-[#C8A24D]" />
                    </div>
                    <p className="font-semibold text-[#0C2340] text-sm">{t("pricing.planLabel")}</p>
                  </div>
                  <p className="text-[#4A5568] text-sm leading-relaxed pl-11">
                    {t("pricing.planDesc")} <span className="font-semibold text-[#0C2340]">{t("pricing.planTotal")}</span> {t("pricing.planSuffix")}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-3.5 mb-8">
                  <p className="text-xs text-[#7A7A8A] font-semibold uppercase tracking-wider mb-4">{t("pricing.includedLabel")}</p>
                  {PRICING_FEATURES.map((feature, idx) => (
                    <div key={feature} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#C8A24D] shrink-0" />
                      <span className="text-[#4A5568] text-sm">{t(`pricing.feature${idx + 1}`)}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  size="lg"
                  className="w-full bg-[#0C2340] hover:bg-[#1B3A5C] text-white font-semibold h-14 text-base rounded-xl transition-all duration-300 group"
                  onClick={() => document.getElementById('viabilidad')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t("pricing.cta")}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <p className="text-center text-xs text-[#7A7A8A] mt-4">
                  {t("pricing.disclaimer")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
